import { useState, useEffect, useRef } from "react";
import { apiClient } from "../../App";
import { Clock, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, AlertCircle, Loader2, Lock, Unlock } from "lucide-react";

/**
 * Timeline Estimate Widget for Calculator Summary Panels.
 * Calls the timeline engine with the calculator's deliverables and shows the estimated range.
 *
 * Props:
 * - deliverables: Array<{ deliverable_type: string, quantity: number }>
 * - complexity: "simple" | "standard" | "luxury" | "ultra_luxury"
 * - isRush: boolean
 * - hasExistingAssets: boolean
 * - variant: "light" (default) or "dark" (for dark-themed summary panels)
 */
export function TimelineEstimateWidget({
  deliverables = [],
  complexity = "standard",
  isRush = false,
  hasExistingAssets = false,
  proposalTotal = null,
  variant = "light",
  // Optional controlled-mode props — when provided, parent owns the override state
  overrideEnabledControlled = undefined,
  overrideWeeksControlled = undefined,
  onOverrideChange = null,
  onEstimateChange = null,
}) {
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);
  const [overrideEnabledLocal, setOverrideEnabledLocal] = useState(false);
  const [overrideWeeksLocal, setOverrideWeeksLocal] = useState("");
  const debounceRef = useRef(null);

  // Resolve controlled vs uncontrolled state
  const isControlled = overrideEnabledControlled !== undefined;
  const overrideEnabled = isControlled ? !!overrideEnabledControlled : overrideEnabledLocal;
  const overrideWeeks = isControlled ? (overrideWeeksControlled ?? "") : overrideWeeksLocal;

  const setOverrideEnabled = (val) => {
    if (isControlled) {
      onOverrideChange && onOverrideChange({ enabled: val, weeks: overrideWeeks });
    } else {
      setOverrideEnabledLocal(val);
      onOverrideChange && onOverrideChange({ enabled: val, weeks: overrideWeeksLocal });
    }
  };
  const setOverrideWeeks = (val) => {
    if (isControlled) {
      onOverrideChange && onOverrideChange({ enabled: overrideEnabled, weeks: val });
    } else {
      setOverrideWeeksLocal(val);
      onOverrideChange && onOverrideChange({ enabled: overrideEnabledLocal, weeks: val });
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!deliverables || deliverables.length === 0) {
      setEstimate(null);
      setError(null);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          mode: "proposal",
          deliverables,
          complexity,
          is_rush: isRush,
          has_existing_assets: hasExistingAssets,
        };
        if (proposalTotal != null && proposalTotal > 0) {
          payload.proposal_total = proposalTotal;
        }
        const overrideNum = parseFloat(overrideWeeks);
        if (overrideEnabled && !isNaN(overrideNum) && overrideNum > 0) {
          payload.override_total_weeks = overrideNum;
        }
        const res = await apiClient.post("/timeline-engine/estimate", payload);
        setEstimate(res.data);
        if (onEstimateChange) onEstimateChange(res.data);
      } catch (err) {
        console.error("Timeline estimate error:", err);
        setError("Unable to estimate timeline");
        setEstimate(null);
        if (onEstimateChange) onEstimateChange(null);
      } finally {
        setLoading(false);
      }
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [JSON.stringify(deliverables), complexity, isRush, hasExistingAssets, proposalTotal, overrideEnabled, overrideWeeks]);

  if (!deliverables || deliverables.length === 0) return null;

  const isDark = variant === "dark";

  const confidenceConfig = {
    high: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-500/10", label: "High Confidence" },
    moderate: { icon: AlertCircle, color: "text-amber-500", bg: "bg-amber-500/10", label: "Moderate" },
    at_risk: { icon: AlertTriangle, color: "text-red-500", bg: "bg-red-500/10", label: "At Risk" },
  };

  if (loading) {
    return (
      <div className={`border-t pt-3 pb-1 ${isDark ? "border-zinc-800" : ""}`} data-testid="timeline-estimate-loading">
        <div className="flex items-center gap-2">
          <Loader2 className={`w-4 h-4 animate-spin ${isDark ? "text-zinc-400" : "text-muted-foreground"}`} />
          <span className={`text-xs ${isDark ? "text-zinc-400" : "text-muted-foreground"}`}>Estimating timeline...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`border-t pt-3 pb-1 ${isDark ? "border-zinc-800" : ""}`} data-testid="timeline-estimate-error">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
          <span className={`text-xs ${isDark ? "text-zinc-500" : "text-muted-foreground"}`}>{error}</span>
        </div>
      </div>
    );
  }

  if (!estimate || !estimate.summary) return null;

  const { summary, confidence, phase_summary, display_settings } = estimate;
  const conf = confidenceConfig[confidence?.level] || confidenceConfig.high;
  const ConfIcon = conf.icon;

  return (
    <div className={`border-t pt-3 pb-1 ${isDark ? "border-zinc-800" : ""}`} data-testid="timeline-estimate-widget">
      {/* Header Row */}
      <button
        className={`flex items-center justify-between w-full group ${isDark ? "hover:bg-white/5" : "hover:bg-muted/30"} -mx-1 px-1 py-1.5 rounded transition-colors`}
        onClick={() => setExpanded(!expanded)}
        data-testid="timeline-estimate-toggle"
      >
        <div className="flex items-center gap-2">
          <Clock className={`w-4 h-4 ${isDark ? "text-indigo-400" : "text-indigo-500"}`} />
          <span className={`text-sm font-semibold ${isDark ? "text-white" : ""}`}>Est. Production Timeline</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold tabular-nums ${isDark ? "text-indigo-300" : "text-indigo-600"}`} data-testid="timeline-range-display">
            {summary.range_display}
          </span>
          {expanded
            ? <ChevronUp className={`w-3.5 h-3.5 ${isDark ? "text-zinc-500" : "text-muted-foreground"}`} />
            : <ChevronDown className={`w-3.5 h-3.5 ${isDark ? "text-zinc-500" : "text-muted-foreground"}`} />
          }
        </div>
      </button>

      {/* Confidence Badge + Staffing + Override Indicator */}
      <div className="flex items-center gap-1.5 mt-1 ml-6 flex-wrap">
        <ConfIcon className={`w-3 h-3 ${conf.color}`} />
        <span className={`text-[11px] ${conf.color}`} data-testid="timeline-confidence">{conf.label}</span>
        {estimate.staffing_allocation?.max_artists && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isDark ? "bg-indigo-500/20 text-indigo-300" : "bg-indigo-50 text-indigo-600 border border-indigo-200"}`} data-testid="staffing-badge">
            {estimate.staffing_allocation.max_artists} artist{estimate.staffing_allocation.max_artists !== 1 ? "s" : ""}
          </span>
        )}
        {isRush && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isDark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700"}`}>RUSH</span>
        )}
        {overrideEnabled && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${isDark ? "bg-white/10 text-white/80" : "bg-slate-100 text-slate-700"}`} data-testid="override-badge">
            <Lock className="w-2.5 h-2.5" /> Override
          </span>
        )}
      </div>

      {/* Expanded Phase Summary */}
      {expanded && phase_summary && phase_summary.length > 0 && (
        <div className={`mt-2.5 ml-6 space-y-1.5 ${isDark ? "text-zinc-400" : "text-muted-foreground"}`} data-testid="timeline-phase-breakdown">
          {/* Override control — premium inline */}
          <div className={`flex items-center justify-between gap-2 mb-2 pb-2 border-b ${isDark ? "border-white/[0.06]" : "border-slate-200/80"}`}>
            <label className={`flex items-center gap-1.5 text-[11px] cursor-pointer select-none ${isDark ? "text-white/60" : "text-slate-600"}`}>
              <input
                type="checkbox"
                checked={overrideEnabled}
                onChange={(e) => setOverrideEnabled(e.target.checked)}
                className="w-3 h-3 accent-indigo-500"
                data-testid="timeline-override-toggle"
              />
              <Unlock className="w-3 h-3" />
              Override Timeline
            </label>
            {overrideEnabled && (
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={overrideWeeks}
                  onChange={(e) => setOverrideWeeks(e.target.value)}
                  placeholder="e.g. 12"
                  className={`w-16 h-7 text-[11px] text-center rounded-md border tabular-nums ${isDark ? "bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30" : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"} focus:outline-none focus:ring-1 focus:ring-indigo-500/50`}
                  data-testid="timeline-override-input"
                />
                <span className={`text-[11px] ${isDark ? "text-white/50" : "text-slate-500"}`}>weeks</span>
              </div>
            )}
          </div>
          {phase_summary.map((phase, idx) => (
            <div key={idx} className="flex items-center justify-between text-[11px]">
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${
                  phase.name.includes("Queue") ? "bg-slate-400" :
                  phase.name === "Kickoff" || phase.name.includes("Kickoff") && !phase.name.includes("Ramp") ? "bg-blue-400" :
                  phase.name.includes("Setup") || (phase.name.includes("Ramp") && !phase.name.includes("Queue")) ? "bg-sky-400" :
                  phase.name.includes("Modeling") || phase.name.includes("Scene") ? "bg-indigo-400" :
                  phase.name.includes("Production") ? "bg-violet-400" :
                  phase.name.includes("Review") ? "bg-amber-400" :
                  phase.name.includes("Dependent") ? "bg-teal-400" :
                  "bg-emerald-400"
                }`} />
                <span>{phase.name}</span>
              </div>
              <span className={`tabular-nums font-medium ${isDark ? "text-zinc-300" : "text-foreground"}`}>
                {phase.week_label}
              </span>
            </div>
          ))}

          {/* Disclaimer */}
          {display_settings?.disclaimer_text && (
            <p className={`text-[10px] mt-2 leading-relaxed italic ${isDark ? "text-zinc-600" : "text-muted-foreground/70"}`}>
              {display_settings.disclaimer_text}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
